import { useQuery } from '@tanstack/react-query';
import { getCoachResourceRevision, listCoachResources, listCoachResourceUses } from '@/lib/api';
import { resourceReadKey } from '@/lib/queryClient';
import type { ResourceListQuery, ResourcePin, ResourceReadScope, ResourceUsesQuery } from '@/lib/types';
import { useFeedbackAuthoringBoundary } from '@/features/feedback-authoring/hooks/useFeedbackAuthoringScope';

/** Only navigation state is retained by the workspace. Protected projections
 * are re-read when this collection or an exact detail is mounted again. */
export type ResourceLibraryState = { query: ResourceListQuery; cursors: (string | undefined)[]; scrollY: number };
export const initialResourceLibraryState = (): ResourceLibraryState => ({ query: { archive: 'all', limit: 20 }, cursors: [undefined], scrollY: 0 });
const readOptions = { staleTime: 0, retry: false, refetchOnWindowFocus: false } as const;
export function useResourceLibrary(scope: ResourceReadScope, state: ResourceLibraryState) {
  const boundary = useFeedbackAuthoringBoundary(scope);
  const options = (query: ResourceListQuery) => ({ ...readOptions,
    queryKey: resourceReadKey(scope, { projection: 'list', query }),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!boundary.current()) throw new DOMException('Resource scope changed', 'AbortError');
      const data = await listCoachResources(query, { scope, signal });
      if (!boundary.current() || signal.aborted) throw new DOMException('Resource scope changed', 'AbortError');
      return data;
    },
  });
  // The Q01 total is filtered. Never use it as an authorized global count.
  const baseline = useQuery({ ...options({ archive: 'all', limit: 20 }), enabled: boundary.active });
  const baselineKnown = boundary.active && baseline.isSuccess && baseline.isFetchedAfterMount;
  const baselineReady = baselineKnown && !baseline.isFetching;
  const advanced = !!state.query.search?.trim() || !!state.query.kind || !!state.query.match_id
    || state.query.without_match === true || !!state.query.source_date
    || (state.query.archive ?? 'all') !== 'all' || (state.query.visible_use ?? 'all') !== 'all'
    || (state.query.publication ?? 'all') !== 'all'
    || (state.query.sort ?? 'updated_desc') !== 'updated_desc' || (state.query.limit ?? 20) !== 20
    || state.cursors.length > 1 || !!state.cursors.at(-1);
  // A retained filter/cursor remains visible and applied even if the authorized
  // collection shrinks. The separate baseline still owns the global count.
  const controls = baselineKnown && (baseline.data.total > 5 || advanced);
  const selectedOptions = options({ ...state.query, cursor: state.cursors.at(-1) });
  const sameSelection = JSON.stringify(selectedOptions.queryKey) === JSON.stringify(options({ archive: 'all', limit: 20 }).queryKey);
  // The default filtered key equals baseline. Do not enable another stale
  // observer from that same query's fetching flag, which would refetch forever.
  const filtered = useQuery({ ...selectedOptions, enabled: controls && !sameSelection });
  const selected = controls && !sameSelection ? filtered : baseline;
  const data = baselineReady && selected.isSuccess && selected.isFetchedAfterMount && !selected.isFetching ? selected.data : null;
  return { active: boundary.active, controls, data,
    authorizedTotal: baselineReady ? baseline.data.total : null,
    loading: boundary.active && (baseline.isFetching || baseline.isPending || controls && filtered.isFetching),
    error: boundary.active && (baseline.isError || controls && !sameSelection && filtered.isError),
    refresh: () => controls && !sameSelection ? Promise.all([baseline.refetch(), filtered.refetch()]) : baseline.refetch(),
  };
}
export function useResourceDetail(scope: ResourceReadScope, pin: ResourcePin, enabled = true) {
  const boundary = useFeedbackAuthoringBoundary(scope);
  const query = useQuery({ ...readOptions,
    queryKey: resourceReadKey(scope, { projection: 'resource', resourceId: pin.resource_id, revisionId: pin.revision_id }),
    enabled: enabled && boundary.active,
    queryFn: async ({ signal }) => {
      if (!boundary.current()) throw new DOMException('Resource scope changed', 'AbortError');
      const data = await getCoachResourceRevision(pin, { scope, signal });
      if (!boundary.current() || signal.aborted || data.resource.resource_id !== pin.resource_id || data.resource.revision_id !== pin.revision_id)
        throw new DOMException('Resource selection changed', 'AbortError');
      return data;
    },
  });
  return { ...query, active: boundary.active, admitted: enabled && boundary.active && query.isSuccess
    && query.isFetchedAfterMount && !query.isFetching ? query.data : null };
}
export function useResourceUses(scope: ResourceReadScope, resourceId: string, selection: ResourceUsesQuery) {
  const boundary = useFeedbackAuthoringBoundary(scope);
  const query = useQuery({ ...readOptions,
    queryKey: resourceReadKey(scope, { projection: 'uses', resourceId, query: selection }), enabled: boundary.active,
    queryFn: async ({ signal }) => {
      if (!boundary.current()) throw new DOMException('Resource scope changed', 'AbortError');
      const data = await listCoachResourceUses(resourceId, selection, { scope, signal });
      if (!boundary.current() || signal.aborted || data.resource_id !== resourceId) throw new DOMException('Resource selection changed', 'AbortError');
      return data;
    },
  });
  return { ...query, active: boundary.active, admitted: boundary.active && query.isSuccess && query.isFetchedAfterMount && !query.isFetching ? query.data : null };
}
